import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button, useMutation, useQuery, useQueryClient } from "@project/ui";
import { Icon, cn } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useActiveHouse } from "@apps/dashboard/features/houses/active-house-context";
import {
  completeAccountOnboarding,
  createHouse,
  createHouseInvitation,
  fetchAccountOnboarding,
  fetchFinancialProfile,
  homeExpenseQueryKeys,
  patchFinancialProfile,
} from "@apps/dashboard/lib/home-expense-api";
import { ApiRequestError } from "@apps/dashboard/lib/api-fetch";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";
import { BrandWordmark } from "@apps/dashboard/components/brand/BrandWordmark";
import { trackDashboardEvent } from "@apps/dashboard/lib/analytics";

const TOTAL_STEPS = 5;

function parseMajorToMinor(input: string): number | null {
  const n = input.trim().replace(/\s/g, "").replace(",", ".");
  if (!n) return null;
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return null;
  return Math.round(v * 100);
}

export default function OnboardingPage(): JSX.Element {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { houses, housesLoading, activeHouseId, activeHouse } = useActiveHouse();

  const [step, setStep] = useState(0);
  const [houseName, setHouseName] = useState("");
  const [houseFormError, setHouseFormError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSent, setInviteSent] = useState(false);
  const [budgetInput, setBudgetInput] = useState("");
  const [budgetError, setBudgetError] = useState<string | null>(null);
  const [startedTracked, setStartedTracked] = useState(false);

  const onboardingQuery = useQuery({
    queryKey: homeExpenseQueryKeys.accountOnboarding(locale),
    queryFn: () => fetchAccountOnboarding(locale),
  });

  const financialQuery = useQuery({
    queryKey:
      activeHouseId != null
        ? homeExpenseQueryKeys.financialProfile(locale, activeHouseId)
        : ["financial", "noop"],
    queryFn: () => {
      if (!activeHouseId) throw new Error("missing house");
      return fetchFinancialProfile(locale, activeHouseId);
    },
    enabled: activeHouseId != null && step >= 3,
  });

  const currency = financialQuery.data?.currency ?? "EUR";

  useEffect(() => {
    if (!startedTracked) {
      void trackDashboardEvent("onboarding_started", { platform: "web" });
      setStartedTracked(true);
    }
  }, [startedTracked]);

  const createMutation = useMutation({
    mutationFn: (name: string) => createHouse(locale, name),
    onSuccess: () => {
      setHouseFormError(null);
      void queryClient.invalidateQueries({ queryKey: homeExpenseQueryKeys.houses(locale) });
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiRequestError ? err.message : t("dashboardUi.auth.errors.generic");
      setHouseFormError(msg);
    },
  });

  const inviteMutation = useMutation({
    mutationFn: () => {
      if (!activeHouseId) throw new Error("missing house");
      return createHouseInvitation(locale, activeHouseId, {
        email: inviteEmail.trim(),
        role: "member",
      });
    },
    onSuccess: () => {
      setInviteError(null);
      setInviteSent(true);
      setInviteEmail("");
      void queryClient.invalidateQueries({
        queryKey: homeExpenseQueryKeys.houseInvitations(locale, activeHouseId!),
      });
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiRequestError ? err.message : t("dashboardUi.auth.errors.generic");
      setInviteError(msg);
    },
  });

  const completeMutation = useMutation({
    mutationFn: () => completeAccountOnboarding(locale),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: homeExpenseQueryKeys.accountOnboarding(locale),
      });
    },
  });

  async function exitWithComplete(startTour: boolean): Promise<void> {
    void trackDashboardEvent("onboarding_completed", {
      platform: "web",
      start_tour: startTour,
    });
    await completeMutation.mutateAsync();
    const suffix = startTour ? "?tour=1" : "";
    navigate(`${dashboardPaths.home}${suffix}`, { replace: true });
  }

  const hasHouse = (houses?.length ?? 0) > 0;
  const step1CanAdvance = hasHouse || createMutation.isSuccess;

  async function handleStep3Next(): Promise<void> {
    setBudgetError(null);
    if (budgetInput.trim() !== "") {
      const minor = parseMajorToMinor(budgetInput);
      if (minor === null) {
        setBudgetError(t("dashboardUi.onboarding.invalidAmount"));
        return;
      }
      if (!activeHouseId) {
        setBudgetError(t("dashboardUi.auth.errors.generic"));
        return;
      }
      try {
        await patchFinancialProfile(locale, activeHouseId, { monthlyBudgetCapMinor: minor });
        void queryClient.invalidateQueries({
          queryKey: homeExpenseQueryKeys.financialProfile(locale, activeHouseId),
        });
        void queryClient.invalidateQueries({
          queryKey: homeExpenseQueryKeys.dashboardSummary(locale, activeHouseId),
        });
      } catch (err: unknown) {
        const msg =
          err instanceof ApiRequestError ? err.message : t("dashboardUi.auth.errors.generic");
        setBudgetError(msg);
        return;
      }
    }
    void trackDashboardEvent("onboarding_step_completed", {
      platform: "web",
      step_index: 3,
    });
    setStep(4);
  }

  if (onboardingQuery.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <LoadingState />
      </div>
    );
  }

  if (onboardingQuery.data?.onboardingCompletedAt) {
    return <Navigate to={dashboardPaths.home} replace />;
  }

  function renderStepBody(): JSX.Element {
    switch (step) {
      case 0:
        return (
          <div className="space-y-4">
            <p className="whitespace-pre-line font-headline text-base font-semibold leading-relaxed text-on-secondary-container">
              {t("dashboardUi.shell.brandSlogan")}
            </p>
            <p className="font-body text-body-md leading-relaxed text-on-surface-variant">
              {t("dashboardUi.onboarding.stepWelcomeBody")}
            </p>
          </div>
        );
      case 1:
        if (housesLoading && !hasHouse) {
          return (
            <div className="py-8">
              <LoadingState />
            </div>
          );
        }
        if (hasHouse && activeHouse) {
          return (
            <div className="space-y-4">
              <p className="font-body text-body-md leading-relaxed text-on-surface-variant">
                {t("dashboardUi.onboarding.stepHouseHasHouseBody", { name: activeHouse.name })}
              </p>
            </div>
          );
        }
        return (
          <div className="space-y-4">
            <p className="font-body text-body-md text-on-surface-variant">
              {t("dashboardUi.onboarding.stepHouseBody")}
            </p>
            <form
              className="flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                const trimmed = houseName.trim();
                if (!trimmed) return;
                createMutation.mutate(trimmed);
              }}
            >
              <div>
                <label htmlFor="onb-house-name" className="text-label-md text-on-surface-variant">
                  {t("dashboardUi.onboarding.houseNameLabel")}
                </label>
                <input
                  id="onb-house-name"
                  value={houseName}
                  onChange={(e) => setHouseName(e.target.value)}
                  placeholder={t("dashboardUi.onboarding.houseNamePlaceholder")}
                  className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
                  autoComplete="off"
                />
              </div>
              <Button type="submit" disabled={createMutation.isPending || !houseName.trim()}>
                {createMutation.isPending
                  ? t("dashboardUi.onboarding.creatingHouse")
                  : t("dashboardUi.houses.createCta")}
              </Button>
            </form>
            {houseFormError ? <p className="text-body-md text-error">{houseFormError}</p> : null}
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <p className="font-body text-body-md text-on-surface-variant">
              {t("dashboardUi.onboarding.stepMembersBody")}
            </p>
            <div>
              <label htmlFor="onb-invite-email" className="text-label-md text-on-surface-variant">
                {t("dashboardUi.onboarding.inviteEmailLabel")}
              </label>
              <input
                id="onb-invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t("dashboardUi.onboarding.inviteEmailPlaceholder")}
                className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
                autoComplete="email"
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              disabled={inviteMutation.isPending || !inviteEmail.trim() || !activeHouseId}
              onClick={() => inviteMutation.mutate()}
            >
              {inviteMutation.isPending
                ? t("dashboardUi.onboarding.inviteSending")
                : t("dashboardUi.onboarding.inviteSend")}
            </Button>
            {inviteError ? <p className="text-body-md text-error">{inviteError}</p> : null}
            {inviteSent ? (
              <p className="text-body-md text-primary">{t("dashboardUi.onboarding.inviteSent")}</p>
            ) : null}
            <p className="text-body-sm text-on-surface-variant">{t("dashboardUi.onboarding.inviteSkipHint")}</p>
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <p className="font-body text-body-md text-on-surface-variant">
              {t("dashboardUi.onboarding.stepBudgetBody")}
            </p>
            {financialQuery.isLoading ? (
              <LoadingState />
            ) : (
              <div>
                <label htmlFor="onb-budget" className="text-label-md text-on-surface-variant">
                  {t("dashboardUi.onboarding.budgetAmountLabel", { currency })}
                </label>
                <input
                  id="onb-budget"
                  inputMode="decimal"
                  value={budgetInput}
                  onChange={(e) => setBudgetInput(e.target.value)}
                  placeholder="0"
                  className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
                  autoComplete="off"
                />
              </div>
            )}
            {budgetError ? <p className="text-body-md text-error">{budgetError}</p> : null}
            <p className="text-body-sm text-on-surface-variant">{t("dashboardUi.onboarding.budgetSkipHint")}</p>
          </div>
        );
      case 4:
        return (
          <div className="space-y-6">
            <p className="font-body text-body-md leading-relaxed text-on-surface-variant">
              {t("dashboardUi.onboarding.stepDoneBody")}
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                type="button"
                className="flex-1"
                disabled={completeMutation.isPending}
                onClick={() => void exitWithComplete(true)}
              >
                {completeMutation.isPending ? t("dashboardUi.onboarding.finishing") : t("dashboardUi.onboarding.startTour")}
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                disabled={completeMutation.isPending}
                onClick={() => void exitWithComplete(false)}
              >
                {completeMutation.isPending ? t("dashboardUi.onboarding.finishing") : t("dashboardUi.onboarding.goHome")}
              </Button>
            </div>
          </div>
        );
      default:
        return <div />;
    }
  }

  const stepHeading =
    step === 0
      ? t("dashboardUi.onboarding.stepWelcomeTitle")
      : step === 1
        ? hasHouse && activeHouse
          ? t("dashboardUi.onboarding.stepHouseHasHouseTitle")
          : t("dashboardUi.onboarding.stepHouseTitle")
        : step === 2
          ? t("dashboardUi.onboarding.stepMembersTitle")
          : step === 3
            ? t("dashboardUi.onboarding.stepBudgetTitle")
            : t("dashboardUi.onboarding.stepDoneTitle");

  return (
    <div className="min-h-screen bg-surface px-4 py-10 font-body text-on-surface">
      <div className="mx-auto max-w-lg">
        <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <BrandWordmark />
            <h1 className="mt-3 font-headline text-2xl font-bold text-on-surface">
              {t("dashboardUi.onboarding.pageTitle")}
            </h1>
            <p className="mt-1 text-label-md text-on-surface-variant">
              {t("dashboardUi.onboarding.progress", { current: step + 1, total: TOTAL_STEPS })}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 self-start"
            disabled={completeMutation.isPending}
            onClick={() => void exitWithComplete(false)}
          >
            {t("dashboardUi.onboarding.skipAll")}
          </Button>
        </header>

        <div className="mb-6 h-1.5 w-full overflow-hidden rounded-full bg-surface-container-low">
          <div
            className="h-full rounded-full bg-primary transition-all duration-300"
            style={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }}
          />
        </div>

        <section
          className={cn(
            "rounded-2xl bg-surface-container-low p-6 shadow-sm outline outline-1 outline-outline-variant/15",
            "sm:p-8",
          )}
        >
          <div className="mb-6 flex items-start gap-3">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Icon name="Sparkles" className="size-6" strokeWidth={1.5} aria-hidden />
            </div>
            <h2 className="font-headline text-xl font-bold text-on-surface">{stepHeading}</h2>
          </div>

          {renderStepBody()}

          {step < 4 ? (
            <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-outline-variant/15 pt-6">
              <Button
                type="button"
                variant="ghost"
                disabled={step === 0 || createMutation.isPending || inviteMutation.isPending}
                onClick={() => setStep((s) => Math.max(0, s - 1))}
              >
                {t("dashboardUi.onboarding.back")}
              </Button>
              <Button
                type="button"
                disabled={
                  (step === 1 && !step1CanAdvance) ||
                  createMutation.isPending ||
                  inviteMutation.isPending ||
                  (step === 3 && financialQuery.isLoading)
                }
                onClick={() => {
                  if (step === 3) {
                    void handleStep3Next();
                  } else {
                    void trackDashboardEvent("onboarding_step_completed", {
                      platform: "web",
                      step_index: step,
                    });
                    setStep((s) => Math.min(TOTAL_STEPS - 1, s + 1));
                  }
                }}
              >
                {t("dashboardUi.onboarding.next")}
              </Button>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
